import React, { useEffect, useRef, useState } from 'react';
import { Activity, ChevronDown, ClipboardPlus, ExternalLink, FileScan } from 'lucide-react';
import type { Client, User } from '../../services/UserService';
import ClinicalEventShortcut from './ClinicalEventShortcut';
import ExamUploadShortcut from './ExamUploadShortcut';

interface ClinicalRecordShortcutMenuProps {
  patientId: string;
  patientName?: string;
  user: User;
  selectedClient: Client;
  onSaved?: () => void | Promise<void>;
  onStartAssessment: () => void;
}

type ClinicalRecordDialog = 'event' | 'exam' | null;

const ClinicalRecordShortcutMenu: React.FC<ClinicalRecordShortcutMenuProps> = ({
  patientId,
  patientName,
  user,
  selectedClient,
  onSaved,
  onStartAssessment,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<ClinicalRecordDialog>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMenuOpen]);

  const openDialog = (dialog: Exclude<ClinicalRecordDialog, null>) => {
    setIsMenuOpen(false);
    setActiveDialog(dialog);
  };

  const handleSaved = async () => {
    setActiveDialog(null);
    await onSaved?.();
  };

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setIsMenuOpen((current) => !current)}
          aria-expanded={isMenuOpen}
          className="flex min-h-16 w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-sky-300 hover:bg-sky-50/30"
        >
          <span className="shrink-0 rounded-lg bg-sky-50 p-2 text-sky-700">
            <ClipboardPlus className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-slate-900">Novo registro clínico</span>
            <span className="block truncate text-xs text-slate-500">Evento, exame ou avaliação</span>
          </span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
        </button>

        {isMenuOpen && (
          <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[65] w-full min-w-[18rem] overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
            <button
              type="button"
              onClick={() => openDialog('event')}
              className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-amber-50"
            >
              <span className="mt-0.5 rounded-lg bg-amber-50 p-1.5 text-amber-700">
                <Activity className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900">Evento clínico</span>
                <span className="block text-xs leading-5 text-slate-500">Hospitalização, exacerbação, alta ou intercorrência.</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => openDialog('exam')}
              className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-teal-50"
            >
              <span className="mt-0.5 rounded-lg bg-teal-50 p-1.5 text-teal-700">
                <FileScan className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900">Anexar exame</span>
                <span className="block text-xs leading-5 text-slate-500">Fotografar páginas, escolher imagens ou enviar PDF.</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                onStartAssessment();
              }}
              className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-sky-50"
            >
              <span className="mt-0.5 rounded-lg bg-sky-50 p-1.5 text-sky-700">
                <ExternalLink className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-slate-900">Nova avaliação</span>
                <span className="block text-xs leading-5 text-slate-500">Abrir Avaliações em outra aba com o paciente selecionado.</span>
              </span>
            </button>
          </div>
        )}
      </div>

      <ClinicalEventShortcut
        hideTrigger
        isOpen={activeDialog === 'event'}
        onOpenChange={(nextIsOpen) => setActiveDialog(nextIsOpen ? 'event' : null)}
        patientId={patientId}
        patientName={patientName}
        clientId={selectedClient.clientId}
        userId={user.userId}
        userDisplayName={user.userDisplayName}
        onSaved={handleSaved}
      />

      <ExamUploadShortcut
        hideTrigger
        isOpen={activeDialog === 'exam'}
        onOpenChange={(nextIsOpen) => setActiveDialog(nextIsOpen ? 'exam' : null)}
        patientId={patientId}
        patientName={patientName}
        user={user}
        selectedClient={selectedClient}
        onSaved={handleSaved}
      />
    </>
  );
};

export default ClinicalRecordShortcutMenu;
